import { camelCase, snakeCase } from 'lodash';
import { compose } from 'lodash/fp';
import { Column } from 'src/columns/createColumn';

import {
    AircompressorConsumption,
    AircompressorPerKm,
    BatterylossesConsumption,
    BatterylossesPerKm,
    CoolingConsumption,
    CoolingPerKm,
    cruiseControlConsumptionInPotential,
    distanceFuelConsumption,
    DrivetrainConsumption,
    DrivetrainPerKm,
    drivingConsumption,
    electricAverageDrivingConsumption,
    electricAverageIdlingConsumption,
    electricAverageOperatingConsumption,
    electricAverageOperatingConsumptionRange,
    electricCruiseControlConsumptionInPotential,
    electricEfficiency,
    electricEfficiencyPerKm,
    electricNoCruiseControlConsumptionInPotential,
    electricOperatingConsumption,
    electricRecuperation,
    electricRecuperationKmPerKwh,
    electricRecuperationPerHour,
    electricRecuperationPerKm,
    EptoConsumption,
    EptoPerKm,
    fuelConsumption,
    fuelEfficiency,
    HvacConsumption,
    HvacPerKm,
    idlingConsumption,
    noCruiseControlConsumptionInPotential,
    operatingFuelConsumption,
    TotalConsumption,
    VpowersupplyConsumption,
    VpowersupplyPerKm,
} from '../columns/consumption';
import {
    excessiveIdlingCountRatio,
    harshAccelerationHardCount,
    harshAccelerationMediumCount,
    harshBrakingHardCount,
    harshBrakingMediumCount,
} from '../columns/count';
import {
    accDistance,
    accelerationPedalDistanceWithCc,
    activeCoastingDistance,
    coastingCcDistance,
    coastingDistance,
    cruiseControlDistanceInPotential,
    cruiseControlPotentialDistance,
    driveProgramActiveDistance,
    driveProgramEfficiencyDistance,
    driveProgramEfficiencyPlusDistance,
    driveProgramEmergencyDistance,
    driveProgramFluidDistance,
    driveProgramHeavyDutyDistance,
    driveProgramManoeuverDistance,
    driveProgramManualDistance,
    driveProgramOffroadDistance,
    driveProgramOffroadWithAssistanceDistance,
    driveProgramPerformanceDistance,
    driveProgramTotalDistance,
    kickdownDistance,
    mileage,
    motorBrakeDistance,
    noAccDistance,
    noCruiseControlDistanceInPotential,
    noPccaDistance,
    overspeedDistance,
    passiveCoastingDistance,
    pccaDistance,
    retarderDistance,
    serviceBrakeDistance,
} from '../columns/distance';
import {
    coloredRatingBar,
    drivers,
    end,
    explicitDrivers,
    start,
    system,
    tripStartEnd,
    vehicle,
    vehicleNames,
    vehicles,
} from '../columns/DOM';
import { drivenElevationDown, drivenElevationUp } from '../columns/height';
import {
    accelerationPedalRating,
    accelerationPedalRatingV2,
    averageSlopeDown,
    averageSlopeUp,
    brakingRating,
    coastingRating,
    cruiseControlRating,
    cruiseControlRatingV2,
    cruiseControlRatingV3,
    electricAccelerationPedalRating,
    electricCruiseControlRating,
    electricForesightedDrivingPercentage,
    electricOverspeedRating,
    electricRecuperationRating,
    excessiveIdlingRating,
    foresightedDrivingPercentage,
    foresightedDrivingPercentageV2,
    fuelForesightedDrivingPercentage,
    harshAccelerationRating,
    harshBrakingRating,
    overspeedRating,
    totalRating,
    totalRatingV2,
    totalRatingV3,
    totalRatingWithoutCC,
    totalRatingWithoutCCV2,
} from '../columns/percentage';
import { operationCondition, scoreSignal } from '../columns/score';
import { averageSpeed } from '../columns/speed';
import {
    accelerationPedalTimeWithCc,
    acceptableIdlingTime,
    accTime,
    activeCoastingTime,
    coastingCCTime,
    coastingTime,
    cruiseControlTimeInPotential,
    driveProgramActiveTime,
    driveProgramEfficiencyPlusTime,
    driveProgramEfficiencyTime,
    driveProgramEmergencyTime,
    driveProgramFluidTime,
    driveProgramHeavyDutyTime,
    driveProgramManoeuverTime,
    driveProgramManualTime,
    driveProgramOffroadTime,
    driveProgramOffroadWithAssistanceTime,
    driveProgramPerformanceTime,
    driveProgramTotalTime,
    drivingTime,
    excessiveIdlingTime,
    harshAccelerationHardTime,
    harshAccelerationMediumTime,
    harshBrakingHardTime,
    harshBrakingMediumTime,
    idlingTime,
    kickdownTime,
    motorBrakeTime,
    noAccTime,
    noCruiseControlTimeInPotential,
    noPccaTime,
    operatingTime,
    passiveCoastingTime,
    pccaTime,
    retarderTime,
    serviceBrakeTime,
} from '../columns/time';
import { averageWeight, co2Emission } from '../columns/weight';
import { columnFormatterMap } from './formatterMap';
import { formatIntegerNumber, withUnit } from './formatters';

export const columns = [
    explicitDrivers,
    drivers,
    vehicle,
    vehicles,
    vehicleNames,
    start,
    end,
    system,
    tripStartEnd,

    distanceFuelConsumption,
    co2Emission,
    idlingConsumption,
    fuelConsumption,
    averageWeight,
    averageSpeed,
    drivenElevationUp,
    drivenElevationDown,

    drivingTime,
    idlingTime,
    operatingTime,
    activeCoastingTime,
    passiveCoastingTime,
    coastingCCTime,
    kickdownTime,
    accelerationPedalTimeWithCc,
    serviceBrakeTime,
    motorBrakeTime,
    retarderTime,
    cruiseControlTimeInPotential,
    noCruiseControlTimeInPotential,
    coastingTime,
    driveProgramActiveTime,
    driveProgramEfficiencyTime,
    driveProgramEfficiencyPlusTime,
    driveProgramEmergencyTime,
    driveProgramFluidTime,
    driveProgramHeavyDutyTime,
    driveProgramManoeuverTime,
    driveProgramManualTime,
    driveProgramOffroadTime,
    driveProgramOffroadWithAssistanceTime,
    driveProgramPerformanceTime,
    driveProgramTotalTime,
    accTime,
    noAccTime,
    pccaTime,
    noPccaTime,
    harshAccelerationMediumTime,
    harshAccelerationHardTime,
    harshBrakingMediumTime,
    harshBrakingHardTime,
    excessiveIdlingTime,
    acceptableIdlingTime,

    averageSlopeDown,
    averageSlopeUp,
    foresightedDrivingPercentage,
    foresightedDrivingPercentageV2,
    coastingRating,
    accelerationPedalRating,
    accelerationPedalRatingV2,
    brakingRating,
    cruiseControlRating,
    cruiseControlRatingV2,
    cruiseControlRatingV3,
    overspeedRating,
    totalRating,
    totalRatingWithoutCC,
    totalRatingV2,
    totalRatingV3,
    totalRatingWithoutCCV2,
    harshAccelerationRating,
    harshBrakingRating,
    excessiveIdlingRating,

    mileage,
    coastingCcDistance,
    kickdownDistance,
    accelerationPedalDistanceWithCc,
    serviceBrakeDistance,
    motorBrakeDistance,
    cruiseControlDistanceInPotential,
    noCruiseControlDistanceInPotential,
    cruiseControlPotentialDistance,
    overspeedDistance,
    activeCoastingDistance,
    passiveCoastingDistance,
    retarderDistance,
    coastingDistance,
    driveProgramActiveDistance,
    driveProgramEfficiencyDistance,
    driveProgramEfficiencyPlusDistance,
    driveProgramEmergencyDistance,
    driveProgramFluidDistance,
    driveProgramHeavyDutyDistance,
    driveProgramManoeuverDistance,
    driveProgramManualDistance,
    driveProgramOffroadDistance,
    driveProgramOffroadWithAssistanceDistance,
    driveProgramPerformanceDistance,
    driveProgramTotalDistance,
    accDistance,
    noAccDistance,
    pccaDistance,
    noPccaDistance,

    noCruiseControlConsumptionInPotential,
    cruiseControlConsumptionInPotential,
    electricCruiseControlConsumptionInPotential,
    electricNoCruiseControlConsumptionInPotential,
    operatingFuelConsumption,
    drivingConsumption,
    operationCondition,
    fuelEfficiency,
    electricEfficiency,

    harshAccelerationMediumCount,
    harshAccelerationHardCount,
    harshBrakingMediumCount,
    harshBrakingHardCount,
    excessiveIdlingCountRatio,

    electricAverageIdlingConsumption,
    electricAverageDrivingConsumption,
    electricAverageOperatingConsumption,
    electricOperatingConsumption,
    electricAverageOperatingConsumptionRange,
    electricRecuperation,
    electricRecuperationPerKm,
    electricRecuperationKmPerKwh,
    electricRecuperationPerHour,
    electricRecuperationRating,
    electricAccelerationPedalRating,
    electricCruiseControlRating,
    electricOverspeedRating,
    electricForesightedDrivingPercentage,

    DrivetrainConsumption,
    DrivetrainPerKm,
    EptoConsumption,
    EptoPerKm,
    BatterylossesConsumption,
    BatterylossesPerKm,
    AircompressorConsumption,
    AircompressorPerKm,
    CoolingConsumption,
    CoolingPerKm,
    HvacConsumption,
    HvacPerKm,
    VpowersupplyConsumption,
    VpowersupplyPerKm,
    TotalConsumption,
    electricEfficiencyPerKm,

    fuelForesightedDrivingPercentage,
];

export const electricColumnNames = [
    'electricAverageOperatingConsumption',
    'electricOperatingConsumption',
    'electricAverageOperatingConsumptionRange',
    'electricAverageDrivingConsumption',
    'electricAverageIdlingConsumption',
    'electricRecuperation',
    'electricEfficiency',
];

export const combustionColumnNames = [
    'operatingFuelConsumption',
    'fuelConsumption',
    'distanceFuelConsumption',
    'drivingConsumption',
    'idlingConsumption',
    'fuelEfficiency',
    'co2Emission',
];

export const commonColumnNames = [
    'operatingTime',
    'drivingTime',
    'idlingTime',
    'mileage',
    'averageWeight',
    'averageSpeed',
];

export const getColumn = (requestedColumn: string, existingColumns = columns) =>
    existingColumns.find((e) => e.key === requestedColumn);
export const getColumns = (requestedColumns: string[], existingColumns = columns) =>
    requestedColumns.reduce((acc: Column[], requestedColumnEl) => {
        const column = getColumn(requestedColumnEl, existingColumns);
        if (column !== undefined) {
            acc.push(column);
        }
        return acc;
    }, []);
/* eslint-disable  @typescript-eslint/no-explicit-any */
export type Discipline = {
    id: string;
    title: Column;
    entries: {
        title: any;
        values: Column[];
    }[];
};

export const hydrateDiscipline = (
    discipline: Discipline,
    attributes: { key: string; unit: string | null }[],
    options?: { plain?: boolean },
): Discipline => {
    return {
        ...discipline,
        title: hydrateColumns([discipline.title], attributes, options)[0],
        entries: discipline.entries.map((entry) => ({
            ...entry,
            values: hydrateColumns(entry.values, attributes, options),
        })),
    };
};

export const hydrateDisciplines = (
    disciplines: Discipline[],
    attributes: { key: string; unit: string | null }[],
    options?: { plain?: boolean },
): Discipline[] => disciplines.map((d) => hydrateDiscipline(d, attributes, options));

export const hydrateColumns = (
    columns: Column[],
    attributes: { key: string; unit: string | null }[],
    options?: { plain?: boolean; ignoreCompose?: boolean },
): Column[] => {
    if (!attributes?.length) {
        return columns;
    }

    const attributeMap = new Map();
    attributes.forEach(({ key, unit }) => {
        attributeMap.set(key, unit);
        attributeMap.set(snakeCase(key), unit);
        attributeMap.set(camelCase(key), unit);
    });

    return columns.map((column) => {
        const ignoreCompose =
            (options && options.ignoreCompose) || false || column.type !== 'rating';
        const rawFormatter = columnFormatterMap[column.key];
        const isRating =
            column.type === 'rating' || [rawFormatter, column.formatter].includes(coloredRatingBar);

        const keysToLookup = [
            column.key,
            column.dataField,
            snakeCase(column.key),
            snakeCase(column.dataField),
            camelCase(column.key),
            camelCase(column.dataField),
        ];

        const unit =
            keysToLookup.reduce((acc, k) => acc ?? attributeMap.get(k), undefined) ?? column.unit;

        const shouldUsePlain = options?.plain && isRating;
        const formatterToUse =
            (shouldUsePlain ? formatIntegerNumber : rawFormatter) || column.formatter;
        const finalUnit = unit || (isRating ? 'pct' : undefined);

        if (finalUnit === column.unit && !isRating) {
            return column;
        }

        const isSpecialFormatter = ([coloredRatingBar, scoreSignal.format] as object[]).includes(
            formatterToUse as object,
        );
        const shouldCompose =
            (!ignoreCompose || column.type !== 'rating') &&
            finalUnit &&
            finalUnit !== 'none' &&
            finalUnit !== column.unit &&
            !isSpecialFormatter;

        return {
            ...column,
            unit: finalUnit,
            formatter: shouldCompose
                ? compose(withUnit(finalUnit), formatterToUse)
                : formatterToUse,
        };
    });
};
